// SCR-017: the audit log (FLOW-009). One compact filter bar (when, customer, action, actor,
// target) + table + a content-free detail dialog + CSV. The server is the real gate on which
// actions a principal may see (API-037's domain filtering, TASK-129); this screen just renders a
// 403 like any other.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError, NetworkError, toApiError } from "@nexus/ui";
import { SessionContext, type Me } from "../../session/session";
import { DataTable, type Column } from "@nexus/ui";
import { ErrorBanner } from "@nexus/ui";
import { RowDetail } from "@nexus/ui";
import { Field, LabelText } from "@nexus/ui";
import { CustomerPicker, useCustomerName } from "../../ui/customer-directory";
import { CopyId, FilterBar, NameCell, PageHeader, Segmented } from "@nexus/ui";
import { MonthRangePicker } from "@nexus/ui";
import { currentMonth, lastMonths, type MonthRange } from "@nexus/ui";
import { actorTitle } from "../../ui/identity";
import { Icon } from "@nexus/ui";
import type { ScreenProps } from "../../router/route";
import {
  actionGroups,
  actionSummary,
  actorLabel,
  actorTypeLabel,
  auditQuery,
  auditRows,
  availableActionPrefixes,
  detailJson,
  EMPTY_AUDIT_FILTERS,
  monthWindow,
  parseTargetFilter,
  targetRef,
  targetText,
  validateAuditFilters,
  type AuditFilters,
  type AuditRowView,
} from "./model";

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  throw error;
}

/** A parsed error body from a non-2xx CSV response, or undefined for a body that is not JSON. */
async function errorBodyOf(response: Response): Promise<unknown> {
  try {
    return JSON.parse(await response.text()) as unknown;
  } catch {
    return undefined;
  }
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
}

function ActorCell({ row, me }: { row: AuditRowView; me: Me | null }) {
  const label = actorLabel(row, me);
  const typeLabel = actorTypeLabel(row.actorType);
  const viaLabel = row.actorVia ? `via ${row.actorVia}` : typeLabel;
  return (
    <span class="name-with-id" title={row.actorEmail ?? actorTitle(row.actorId)}>
      <span>{label}</span>
      <span class="cell-sub">{viaLabel}</span>
    </span>
  );
}

function ActionCell({ row }: { row: AuditRowView }) {
  const summary = actionSummary(row);
  if (!summary) return <code class="nowrap">{row.action}</code>;
  return (
    <span class="name-with-id">
      <span>{summary}</span>
      <code class="cell-sub nowrap">{row.action}</code>
    </span>
  );
}

/** The target's name over its kind; its id only on hover and in the row detail. */
function TargetCell({ row }: { row: AuditRowView }) {
  return (
    <span class="name-with-id" title={targetRef(row)}>
      <span>{targetText(row)}</span>
      <span class="cell-sub mono">{row.targetType}</span>
    </span>
  );
}

function CustomerCell({ orgId }: { orgId: string | null }) {
  const name = useCustomerName(orgId);
  if (!orgId) return <span class="muted">—</span>;
  return <NameCell name={name} id={orgId} />;
}

type WhenMode = "all" | "months";

export function Audit(_props: ScreenProps<"/audit">) {
  const client = useApi();
  const services = useContext(SessionContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const me = session?.me ?? null;

  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [months, setMonths] = useState<MonthRange | null>(null);
  const [targetInput, setTargetInput] = useState("");
  const [rows, setRows] = useState<AuditRowView[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [selected, setSelected] = useState<AuditRowView | null>(null);
  const [rawEvents, setRawEvents] = useState<Map<number, unknown>>(new Map());
  const [exportError, setExportError] = useState<ApiError | null>(null);
  const [exporting, setExporting] = useState(false);

  const fieldErrors = validateAuditFilters(filters);
  const valid = Object.keys(fieldErrors).length === 0;

  const load = (cursor: string | null, append: boolean) => {
    if (!valid) return;
    setLoading(true);
    setError(null);
    client
      .get("/v1/admin/audit-events", { query: auditQuery(filters, cursor) })
      .then((page) => {
        const shaped = auditRows(page.items);
        setRows((current) => (append ? [...current, ...shaped] : shaped));
        setRawEvents((current) => {
          const next = append ? new Map(current) : new Map<number, unknown>();
          for (const item of page.items) next.set(item.id, item.detail);
          return next;
        });
        setNextCursor(page.nextCursor ?? null);
      })
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(null, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.from, filters.to, filters.orgId, filters.actorId, filters.action, filters.targetType, filters.targetId]);

  const prefixes = session ? availableActionPrefixes(session.me.role, session.me.permissions) : [];
  const groups = actionGroups(prefixes, filters.action);

  const chooseMonths = (range: MonthRange | null) => {
    setMonths(range);
    setFilters((current) => ({ ...current, ...monthWindow(range) }));
  };

  // A plain download anchor would save a 422 error body as if it were the file (API-037
  // `export_too_large` over a window with too many rows to export): fetched here instead, so a
  // refused export shows the same clear error banner as any other failed call, and only a
  // successful body is ever saved.
  const exportCsv = () => {
    setExportError(null);
    setExporting(true);
    fetch(client.csvUrl("/v1/admin/audit-events", { query: auditQuery(filters) }), { credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw toApiError(response.status, await errorBodyOf(response));
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
          const link = document.createElement("a");
          link.href = objectUrl;
          link.download = "audit-events.csv";
          link.click();
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      })
      .catch((err: unknown) => setExportError(err instanceof ApiError ? err : new NetworkError()))
      .finally(() => setExporting(false));
  };

  const columns: Column<AuditRowView>[] = [
    { key: "occurredAt", header: "Time", cell: (row) => <span class="nowrap">{when(row.occurredAt)}</span>, compare: (a, b) => a.occurredAt.localeCompare(b.occurredAt) },
    { key: "actorId", header: "Actor", help: "audit.actor", cell: (row) => <ActorCell row={row} me={me} /> },
    { key: "action", header: "Action", help: "audit.action", cell: (row) => <ActionCell row={row} />, compare: (a, b) => a.action.localeCompare(b.action) },
    { key: "orgId", header: "Customer", cell: (row) => <CustomerCell orgId={row.orgId} /> },
    { key: "target", header: "Target", help: "audit.target", cell: (row) => <TargetCell row={row} /> },
  ];

  const whenMode: WhenMode = months ? "months" : "all";

  return (
    <div class="page audit-screen">
      <PageHeader
        title="Audit log"
        lede="Every change made in the admin app: who made it, when, and what it touched. Newest first; you see the areas your role may read."
      />
      <FilterBar
        end={
          <button type="button" class="button button-secondary" disabled={exporting} onClick={exportCsv}>
            <Icon name="download" />
            {exporting ? "Exporting…" : "Download CSV"}
          </button>
        }
      >
        <div class="field field-compact audit-when">
          <LabelText id="audit-when-label" label="When" help="audit.when" compact />
          <div class="row">
            <Segmented<WhenMode>
              labelledBy="audit-when-label"
              options={[
                { value: "all", label: "All time" },
                { value: "months", label: "Months" },
              ]}
              value={whenMode}
              onChange={(mode) => chooseMonths(mode === "all" ? null : lastMonths(3, currentMonth(new Date())))}
            />
            {months && <MonthRangePicker id="audit-months" labelledBy="audit-when-label" value={months} onChange={chooseMonths} />}
          </div>
        </div>
        <Field id="audit-org" label="Customer" help="common.customer" compact>
          <CustomerPicker id="audit-org" value={filters.orgId} allowAll onChange={(orgId) => setFilters({ ...filters, orgId })} />
        </Field>
        <Field id="audit-action" label="Action" help="audit.action" compact>
          <select id="audit-action" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.currentTarget.value })}>
            <option value="">Any action</option>
            {groups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.actions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </Field>
        <Field id="audit-actor" label="Actor" help="audit.actor" compact>
          <input
            id="audit-actor"
            type="search"
            autocomplete="off"
            spellcheck={false}
            placeholder="Email or id"
            value={filters.actorId}
            onInput={(e) => setFilters({ ...filters, actorId: e.currentTarget.value })}
          />
        </Field>
        <Field id="audit-target" label="Target" help="audit.target" compact>
          <input
            id="audit-target"
            type="search"
            class="input-mono"
            autocomplete="off"
            spellcheck={false}
            placeholder="type or type/id"
            value={targetInput}
            onInput={(e) => {
              const text = e.currentTarget.value;
              setTargetInput(text);
              setFilters({ ...filters, ...parseTargetFilter(text) });
            }}
          />
        </Field>
      </FilterBar>
      {exportError && <ErrorBanner error={exportError} onRetry={exportCsv} />}

      <div class="table-card">
        <DataTable
          caption="Admin audit events"
          hideCaption
          columns={columns}
          rows={rows}
          rowKey={(row) => String(row.id)}
          loading={loading}
          error={error}
          onRetry={() => load(null, false)}
          emptyMessage="No audit events match these filters."
          nextCursor={nextCursor}
          onLoadMore={() => load(nextCursor, true)}
          onRowClick={setSelected}
          rowLabel={(row) => `Event ${row.id}, ${row.action}`}
        />
      </div>

      {selected && (
        <RowDetail
          title={`Event #${selected.id} — ${selected.action}`}
          open
          onClose={() => setSelected(null)}
          class="dialog-wide"
          facts={[
            { label: "Time", value: when(selected.occurredAt) },
            {
              label: "Actor",
              value: (
                <>
                  {actorLabel(selected, me)} <span class="muted">({actorTypeLabel(selected.actorType)})</span>
                </>
              ),
            },
            { label: "Customer", value: <CustomerCell orgId={selected.orgId} /> },
            {
              label: "Target",
              value: (
                <>
                  {targetText(selected)} <span class="muted mono">({selected.targetType})</span>{" "}
                  <CopyId id={selected.targetId} label="" what="target ID" />
                </>
              ),
            },
            { label: "Request id", value: <span class="mono">{selected.requestId ?? "—"}</span> },
            { label: "Source IP", value: <span class="mono">{selected.sourceIp ?? "—"}</span> },
          ]}
        >
          <h3>Detail (before and after)</h3>
          <pre class="audit-detail">{detailJson({ detail: (rawEvents.get(selected.id) ?? null) as Record<string, unknown> | null })}</pre>
        </RowDetail>
      )}
    </div>
  );
}
